import { AppRoute } from '@agentselly/immodossier-shared';
import type { QueryClient } from '@tanstack/react-query';

export const PROPERTY_RELATED_QUERY_ROUTES: readonly AppRoute[] = [
  AppRoute.$LANG_$COMPANYID_$CONTACTID_DOSSIER_$DOSSIERID_HOME,
  AppRoute.$LANG_$COMPANYID_$CONTACTID_DOSSIER_$DOSSIERID_EVALUATION,
  AppRoute.$LANG_$COMPANYID_$CONTACTID_DOSSIER_$DOSSIERID_PROPERTY_EDIT,
  AppRoute.$LANG_$COMPANYID_$CONTACTID_DOSSIER_$DOSSIERID_PROPERTY_CONDITION_EDIT,
  AppRoute.$LANG_$COMPANYID_$CONTACTID_DOSSIER_$DOSSIERID_PROPERTY_QUALITIES_EDIT,
  AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_HOME,
];

export interface InvalidatePropertyQueriesOptions {
  dossierId: string;
  propertyId?: string;
  contactId?: string;
}

function hasParam(queryKey: unknown, name: string, value: string): boolean {
  return (
    Array.isArray(queryKey) &&
    queryKey.some(
      (part) =>
        typeof part === 'object' &&
        part !== null &&
        (part as Record<string, unknown>)[name] === value,
    )
  );
}

export function isPropertyQueryMatchingEntity(
  queryKey: unknown,
  dossierId: string,
  propertyId?: string,
): boolean {
  if (!Array.isArray(queryKey) || typeof queryKey[0] !== 'string') {
    return false;
  }
  const route = queryKey[0];
  const routeLower = route.toLowerCase();

  const isDossierRoute =
    routeLower.includes('dossier') ||
    PROPERTY_RELATED_QUERY_ROUTES.includes(route as AppRoute);
  if (
    dossierId &&
    isDossierRoute &&
    hasParam(queryKey, 'dossierId', dossierId)
  ) {
    return true;
  }

  const targetPropId = propertyId || dossierId;
  const isCockpitRoute =
    routeLower.includes('cockpit') ||
    route === AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_HOME;
  return (
    !!targetPropId &&
    isCockpitRoute &&
    hasParam(queryKey, 'propertyId', targetPropId)
  );
}

export async function invalidateEvaluationQueries(
  queryClient: QueryClient,
  options: InvalidatePropertyQueriesOptions,
): Promise<void> {
  await invalidatePropertyRelatedQueries(queryClient, options);
}

export async function invalidatePropertyRelatedQueries(
  queryClient: QueryClient,
  options?: InvalidatePropertyQueriesOptions | string,
): Promise<void> {
  const dossierId = typeof options === 'string' ? options : options?.dossierId;
  const propertyId =
    typeof options === 'object' ? options?.propertyId : undefined;

  const contactId =
    typeof options === 'object' ? options?.contactId : undefined;

  await queryClient.invalidateQueries({
    predicate: (query) =>
      (query.queryKey[0] === AppRoute.$LANG_$COMPANYID_$CONTACTID_INDEX &&
        !!contactId &&
        hasParam(query.queryKey, 'contactId', contactId)) ||
      isPropertyQueryMatchingEntity(
        query.queryKey,
        dossierId ?? '',
        propertyId,
      ),
    refetchType: 'all',
  });
}
